import React, { useMemo, useRef } from "react";
import { useQuery } from "react-query";
import { Field, useForm, useFormState } from "react-final-form";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { Theme } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";
import clsx from "clsx";
import WeduCheckbox from "../../../../../components/WeduCheckbox/WeduCheckbox";
import WeduTooltip from "../../../../../components/WeduTooltip/intex";
import InfoIcon from "../../../../../components/InfoIcon";
import { QUERY_KEYS } from "../../../../../constants";
import useBranchQueries from "../../../../school/hooks/useBranchQueries";
import { useSchoolId } from "../../../../school/hooks/useSchoolId";
import { ApplicationStatus } from "../../../../application/types";
import { WizardMode } from "../../../../wizard/types";
import { useWizard } from "../../../../wizard/context";
import LabeledDot from "../../../../../components/LabeledDot/LabeledDot";
import { useBranchConfig } from "../../../../branch/hooks/useBranchConfig";

const useStyles = makeStyles((theme: Theme) => ({
  titlesContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: 8,
  },
  mainTitle: {
    color: "#535358",
    fontSize: 18,
    fontWeight: "bold",
  },
  title: {
    marginLeft: 377,
    color: "#a1a4ac",
    fontSize: 14,
  },
  row: {
    height: 40,
    display: "flex",
    borderTop: "1px solid #FFF",
    borderBottom: "1px solid #FFF",
    "&:hover": {
      borderTop: "1px solid #DBDBDB",
      borderBottom: "1px solid #DBDBDB",
    },
  },
  leftContainer: {
    minWidth: 545,
    height: "100%",
    display: "flex",
    alignItems: "center",
  },
  rightContainer: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    paddingLeft: 16,
  },

  root: {
    paddingBottom: 21,
    marginBottom: -18,
    "label + &": {
      marginTop: 4,
    },
  },
  checkboxContainer: {
    paddingLeft: 2,
    "& .MuiFormControlLabel-root": {
      marginRight: "unset",
      "& .MuiFormControlLabel-label": {
        maxWidth: 520,
        height: 20,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },
  },
  checkboxContainerEditMode: {
    "& .MuiFormControlLabel-root": {
      "& .MuiFormControlLabel-label": {
        maxWidth: 640,
      },
    },
  },
  appContainer: {
    width: 110,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: 16,
  },
  asterisk: {
    color: "#535358",
    lineHeight: "inherit",
    fontSize: 16,
    fontWeight: "normal",
    marginLeft: 3,
  },
  hasError: {
    color: theme.palette.error.main,
  },
}));

interface IParsedBranch {
  id: string;
  name: string;
  approved: string;
  pending: string;
  hasApp: boolean;
  isDeactivationNoteRequired: boolean;
}

const AssignBranches = (props) => {
  const classes = useStyles();

  const { values } = useFormState();
  const { change } = useForm();
  const { openWizard } = useWizard();
  const { fetchAll } = useBranchQueries();
  const schoolId = useSchoolId();

  const { branches } = QUERY_KEYS;

  const { data } = useQuery<any>(`${branches}-${schoolId}`, () => fetchAll({ schoolId }));
  const { getDeactivateConfig } = useBranchConfig();

  const {
    fields: { name },
    meta: { submitError, invalid, submitFailed, dirtySinceLastSubmit },
    stepName,
  } = props;
  const hasError = useMemo(
    () => invalid && submitFailed && !dirtySinceLastSubmit,
    [invalid, dirtySinceLastSubmit, submitFailed],
  );

  const allBranches = data?.data || [];
  const mode = values?.mode;
  const isEditMode = mode === WizardMode.Edit;
  const branchesWithAppCount =
    values?.[stepName]?.branchesWithAppCount || {};
  const selectedBranches =
    values?.[stepName]?.branches || [];

  const { current: initiallySelectedBranches } = useRef([...selectedBranches]);

  const parsedBranches: IParsedBranch[] = allBranches.map((branch) => {
    const activeBranch = branchesWithAppCount[branch.id];
    const approved = activeBranch?.applicationsCount?.approved;
    const pending = activeBranch?.applicationsCount?.pending;
    const hasApp = (approved ?? 0) + (pending ?? 0) > 0;

    return {
      id: branch.id,
      name: branch.name,
      approved: approved === undefined ? "N/A" : String(approved),
      pending: pending === undefined ? "N/A" : String(pending),
      hasApp: hasApp,
      isDeactivationNoteRequired:
        selectedBranches.includes(branch.id) &&
        initiallySelectedBranches.includes(branch.id), // requires deactivation note when this branch is unchecked
    };
  });

  const deactivateHandler = async ({
    isDeactivationNoteRequired,
    hasApp,
    id,
    approved,
    pending,
  }: IParsedBranch) => {
    if (!isDeactivationNoteRequired) return;

    if (hasApp) {
      const result = await openWizard(
        getDeactivateConfig({ approved, pending }),
      );

      if (result) {
        change(
          `${name}`,
          selectedBranches.filter((branch) => branch !== id),
        );
        change(
          `${stepName}.branchesWithAppCount.${id}.deactivationNote`,
          { text: result.reason },
        );
      }

      return;
    }

    change(
      `${stepName}.branchesWithAppCount.${id}.deactivationNote`,
      { text: "" },
    );
  };

  return (
    <div>
      <div className={classes.titlesContainer}>
        <div className={classes.mainTitle}>
          Assign to Branches
          <Typography
            className={clsx(
              classes.asterisk,
              hasError && submitError && classes.hasError,
            )}
            component={"span"}
            variant={"subtitle2"}
          >
            *
          </Typography>
          <InfoIcon
            text="You need to assign to the branches where you have those programs"
            portalMode={false}
          />
        </div>
        {isEditMode && (
          <Typography className={classes.title} component={"span"}>
            Applications
          </Typography>
        )}
      </div>
      {parsedBranches.map((branch) => (
        <div key={branch.id} className={classes.row}>
          <div className={classes.leftContainer}>
            <div
              className={clsx(
                classes.checkboxContainer,
                !isEditMode && classes.checkboxContainerEditMode,
              )}
            >
              <WeduTooltip
                title={
                  branch.name.length > (isEditMode ? 50 : 60) ? branch.name : ""
                }
              >
                <Field
                  value={branch.id}
                  label={branch.name}
                  {...(branch.isDeactivationNoteRequired &&
                    branch.hasApp && { onChange: (e) => e.preventDefault() })}
                  onClick={() => deactivateHandler(branch)}
                  type="checkbox"
                  name={name}
                  component={WeduCheckbox}
                />
              </WeduTooltip>
            </div>
          </div>
          {isEditMode && (
            <div className={classes.rightContainer}>
              <div className={classes.appContainer}>
                <LabeledDot
                  color={ApplicationStatus.APPROVED}
                  tooltip="Approved applications"
                  label={branch.approved}
                />
                <LabeledDot
                  color={ApplicationStatus.PENDING}
                  tooltip="Pending applications"
                  label={branch.pending}
                />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default AssignBranches;
